import React, { memo, useMemo } from 'react';
import UnifiedPrintModal from '@/components/Print/UnifiedPrintModal';
import type { InvoiceData } from '@/components/Print/InvoiceContent';

/** An advance (deposit) the patient handed over, or money given back to them — printed as proof */
export interface DepositReceipt {
  /** Money given back: a refund receipt the patient signs */
  refund?: boolean;
  /** Why it was refunded */
  reason?: string | null;
  /** The bill the refund was made on */
  invoice_number?: string | null;
  patient_id: string;
  clinic_id: string;
  /** Whose letterhead and clinic details to print: the patient's or admitting doctor */
  doctor_id?: string;
  amount: number;
  payment_mode?: string;
  reference_number?: string | null;
  date?: string;
  /** IPD number, when the deposit is for a stay */
  admission_number?: string | null;
  /** CH-RCT-2026-27-0001 / CH-RFD-…; none on receipts taken before numbering began */
  receipt_number?: string | null;
}

const MODE_LABEL: Record<string, string> = {
  cash: 'Cash', upi: 'UPI', card: 'Card', netbanking: 'Bank transfer', cheque: 'Cheque', other: 'Other',
};

interface Props {
  open: boolean;
  deposit: DepositReceipt | null;
  onClose: () => void;
}

// The bill print layout, titled "Advance receipt" (or "Refund receipt"), with one line for the money
function PrintDepositReceiptModal({ open, deposit, onClose }: Props) {
  const title = deposit?.refund ? 'Refund receipt' : 'Advance receipt';
  const data = useMemo<InvoiceData | null>(() => deposit && {
    patient_id:   deposit.patient_id,
    clinic_id:    deposit.clinic_id,
    doctor_id:    deposit.doctor_id,
    doc_title:    title,
    invoice_number: deposit.receipt_number || undefined,
    invoice_date: deposit.date ?? new Date().toISOString(),
    subtotal:     deposit.amount,
    total_amount: deposit.amount,
    paid_amount:  deposit.refund ? undefined : deposit.amount,
    payment_mode: [MODE_LABEL[deposit.payment_mode ?? ''] ?? deposit.payment_mode, deposit.reference_number && `Ref ${deposit.reference_number}`]
                    .filter(Boolean).join(' · ') || undefined,
    ...(deposit.refund ? {
      party_label:     'Paid to',
      payment_label:   'Refunded',
      recipient_signs: true,
      notes:           deposit.reason || undefined,
      items: [{
        service_name: deposit.invoice_number ? `Refund on bill ${deposit.invoice_number}`
                    : deposit.admission_number ? `Refund of deposit for ${deposit.admission_number}` : 'Refund of advance',
        category:     'Refund',
        quantity:     1,
        unit_price:   deposit.amount,
      }],
    } : {
      notes: 'Held as advance: used towards the patient\'s bills; any balance is refunded.',
      items: [{
        service_name: deposit.admission_number ? `Deposit for ${deposit.admission_number}` : 'Advance deposit',
        category:     'Advance',
        quantity:     1,
        unit_price:   deposit.amount,
      }],
    }),
  }, [deposit, title]);

  return <UnifiedPrintModal open={open} onClose={onClose} type="invoice" data={data} title={title} />;
}

export default memo(PrintDepositReceiptModal);
